import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getDive, getDiveProfile, updateDive, deleteDive, getDiveSuggestions, getDivePhotos, uploadDivePhotos, deleteDivePhoto, updatePhotoCaption, getBuddies, getVisibility, getLookup, identifyPhoto, getPhotoSpecies, getAppSetting, deletePhotoSpecies, addManualSpecies, updateSpeciesDutchName, getDives, getDiveStamps, uploadDiveStamp, updateStampName, deleteDiveStamp } from '../api';
import offlineCache from '../offlineCache';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const SERIES = [
  { key: 'diepte',  label: 'Diepte (m)',      color: '#0077b6', type: 'area' },
  { key: 'temp',    label: 'Temp (°C)',       color: '#f59e0b', type: 'line' },
  { key: 'ndt',     label: 'NDT (min)',       color: '#22c55e', type: 'line' },
  { key: 'cns',     label: 'CNS (%)',         color: '#ef4444', type: 'line' },
  { key: 'opstijg', label: 'Snelheid (m/min)',color: '#f97316', type: 'line' },
];

// Kleur op basis van snelheid (m/min)
function speedColor(v) {
  if (v == null) return null;
  if (v > 9)    return 'red';
  if (v >= 0)   return 'green';
  if (v >= -3)  return 'gray';
  return 'blue';
}

// Bereken profieldata inclusief opstijgsnelheid (3-punt voortschrijdend gemiddelde)
// Geeft ook o_red / o_green / o_gray / o_blue mee voor gekleurde lijnweergave.
// Boundary blending: een punt wordt ook opgenomen in de aangrenzende kleurrange
// zodat de overgang naadloos is (geen gaten tussen kleursegmenten).
function calcProfile(waypoints) {
  const raw = waypoints.map((wp, i) => {
    let opstijg = null;
    if (i > 0) {
      const prev  = waypoints[i - 1];
      const dtSec = wp.divetime_sec - prev.divetime_sec;
      if (dtSec > 0) {
        opstijg = (prev.depth_m - wp.depth_m) / (dtSec / 60); // m/min, positief = stijgen
      }
    }
    return {
      t:       Math.round(wp.divetime_sec / 60),
      diepte:  -(wp.depth_m),
      temp:    wp.temperature_c,
      ndt:     wp.nodecotime_min,
      cns:     wp.cns_percent,
      opstijg,
    };
  });

  // Pass 2: smooth over 3 punten
  const smoothed = raw.map((wp, i, arr) => {
    if (wp.opstijg === null) return wp;
    const vals = [arr[i - 1]?.opstijg, wp.opstijg, arr[i + 1]?.opstijg]
      .filter(v => v != null);
    return { ...wp, opstijg: Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 };
  });

  // Pass 3: kleurrange-velden met boundary blending voor naadloze overgangen
  return smoothed.map((wp, i, arr) => {
    const v = wp.opstijg;
    if (v == null) return { ...wp, o_red: null, o_green: null, o_gray: null, o_blue: null };
    const myColor   = speedColor(v);
    const prevColor = speedColor(arr[i - 1]?.opstijg);
    const nextColor = speedColor(arr[i + 1]?.opstijg);
    return {
      ...wp,
      o_red:   (myColor === 'red'   || prevColor === 'red'   || nextColor === 'red')   ? v : null,
      o_green: (myColor === 'green' || prevColor === 'green' || nextColor === 'green') ? v : null,
      o_gray:  (myColor === 'gray'  || prevColor === 'gray'  || nextColor === 'gray')  ? v : null,
      o_blue:  (myColor === 'blue'  || prevColor === 'blue'  || nextColor === 'blue')  ? v : null,
    };
  });
}

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

export default function DiveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [dive, setDive]             = useState(null);
  const [profile, setProfile]       = useState([]);
  const [editing, setEditing]       = useState(false);
  const [form, setForm]             = useState({});
  const [loading, setLoading]       = useState(true);
  const [suggestions, setSuggestions]   = useState({ buddies: [], suitTypes: [] });
  const [buddyList, setBuddyList]           = useState([]);
  const [buddyObjects, setBuddyObjects]     = useState([]);
  const [visibilityList, setVisibilityList] = useState([]);
  const [lookups, setLookups]               = useState({});
  const [photos, setPhotos]           = useState([]);
  const [stamps, setStamps]           = useState([]);
  const [lightbox, setLightbox]       = useState(null); // filename
  const [uploading, setUploading]     = useState(false);
  const [visible, setVisible]         = useState({ diepte: true, temp: true, ndt: false, cns: false, opstijg: false });
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null }); // {id, dive_number}

  // Per-duik kaartvoorkeur (laag + zoom) — opgeslagen in localStorage
  const mapPrefKey = `dl_map_${id}`;
  const mapPref    = (() => { try { return JSON.parse(localStorage.getItem(mapPrefKey)) || {}; } catch { return {}; } })();
  const [diveMapLayer, setDiveMapLayer] = useState(mapPref.layer || 'satellite');
  const [diveMapZoom,  setDiveMapZoom]  = useState(mapPref.zoom  || 14);
  const saveDiveMapPref = (layer, zoom) => {
    try { localStorage.setItem(mapPrefKey, JSON.stringify({ layer, zoom })); } catch {}
  };

  // GPS picker
  const [gpsPicker,    setGpsPicker]   = useState(false);
  const [gpsDraft,     setGpsDraft]    = useState({ lat: 20, lng: 10 });
  const [gpsSaving,    setGpsSaving]   = useState(false);
  const [mapLayer,     setMapLayer]    = useState('satellite'); // 'satellite' | 'map'
  const [gpsFromPhoto, setGpsFromPhoto] = useState(null);      // { lat, lng } uit EXIF
  const [citySearch,   setCitySearch]  = useState('');
  const [cityResults,  setCityResults] = useState([]);
  const [citySearching, setCitySearching] = useState(false);
  const [flyTo,        setFlyTo]       = useState(null);       // { lat, lng } om kaart naartoe te sturen

  useEffect(() => {
    getDiveSuggestions()
      .then(data => { offlineCache.set(offlineCache.KEYS.SUGGESTIONS, data); setSuggestions(data); })
      .catch(() => { const c = offlineCache.get(offlineCache.KEYS.SUGGESTIONS); if (c) setSuggestions(c); });

    getDives().then(all => {
      const sorted = [...all].sort((a, b) =>
        String(b.dive_date).localeCompare(String(a.dive_date)) ||
        (b.dive_number ?? 0) - (a.dive_number ?? 0)
      );
      const idx = sorted.findIndex(d => String(d.id) === String(id));
      setNeighbors({
        next: idx > 0              ? { id: sorted[idx - 1].id, num: sorted[idx - 1].dive_number } : null,
        prev: idx < sorted.length - 1 ? { id: sorted[idx + 1].id, num: sorted[idx + 1].dive_number } : null,
      });
    }).catch(() => {});

    getBuddies().then(data => {
      setBuddyObjects(data);
      setBuddyList(data.map(b => b.name));
    }).catch(() => {});
    getVisibility().then(data => setVisibilityList(data)).catch(() => {});
    ['problemen','soort','reis','thema','soort_fles','inspanning','pak'].forEach(cat =>
      getLookup(cat).then(items => setLookups(prev => ({ ...prev, [cat]: items.map(i => i.value) }))).catch(() => {})
    );

    getDivePhotos(id)
      .then(data => { offlineCache.set(offlineCache.KEYS.photos(id), data); setPhotos(data); })
      .catch(() => { const c = offlineCache.get(offlineCache.KEYS.photos(id)); if (c) setPhotos(c); });

    getDiveStamps(id).then(setStamps).catch(() => {});

    Promise.all([getDive(id), getDiveProfile(id)])
      .then(([d, p]) => {
          offlineCache.set(offlineCache.KEYS.dive(id), d);
          offlineCache.set(offlineCache.KEYS.profile(id), p);
          setDive(d);
          setForm({
          location_name:  d.location_name  || '',
          buddy:          d.buddy          || '',
          visibility_m:   d.visibility_m   || '',
          water_type:     d.water_type     || 'zout',
          suit_type:      d.suit_type      || '',
          weight_kg:      d.weight_kg      || '',
          rating:         d.rating         || '',
          notes:          (typeof d.notes === 'string' ? d.notes : '') || '',
          tank_volume_l:  d.tank_volume_l  || '',
          problems:       d.problems       || '',
          dive_type:      d.dive_type      || '',
          trip:           d.trip           || '',
          theme:          d.theme          || '',
          comfort_rating: d.comfort_rating || '',
          tank_type:      d.tank_type      || '',
          exertion:       d.exertion       || '',
        });
        setProfile(calcProfile(p));
      })
      .catch(() => {
        const d = offlineCache.get(offlineCache.KEYS.dive(id));
        const p = offlineCache.get(offlineCache.KEYS.profile(id));
        if (d) {
          setDive(d);
          setForm({
            location_name:  d.location_name  || '',
            buddy:          d.buddy          || '',
            visibility_m:   d.visibility_m   || '',
            water_type:     d.water_type     || 'zout',
            suit_type:      d.suit_type      || '',
            weight_kg:      d.weight_kg      || '',
            rating:         d.rating         || '',
            notes:          (typeof d.notes === 'string' ? d.notes : '') || '',
            tank_volume_l:  d.tank_volume_l  || '',
            problems:       d.problems       || '',
            dive_type:      d.dive_type      || '',
            trip:           d.trip           || '',
            theme:          d.theme          || '',
            comfort_rating: d.comfort_rating || '',
            tank_type:      d.tank_type      || '',
            exertion:       d.exertion       || '',
          });
        }
        if (p) {
          setProfile(p.map(wp => ({
            t:      Math.round(wp.divetime_sec / 60),
            diepte: -(wp.depth_m),
            temp:   wp.temperature_c,
            ndt:    wp.nodecotime_min,
            cns:    wp.cns_percent,
          })));
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const toggleSeries = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));

  const [saveError, setSaveError] = useState(null);

  const save = async () => {
    setSaveError(null);
    try {
      await updateDive(id, form);
      setDive({ ...dive, ...form });
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Opslaan mislukt');
    }
  };

  const remove = async () => {
    if (!window.confirm('Duik verwijderen?')) return;
    await deleteDive(id);
    navigate('/dives');
  };

  const searchCity = async (q) => {
    if (!q.trim()) { setCityResults([]); return; }
    setCitySearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'nl,en' } }
      );
      const data = await res.json();
      setCityResults(data.map(r => ({
        label: r.display_name,
        lat:   parseFloat(r.lat),
        lng:   parseFloat(r.lon),
      })));
    } catch { setCityResults([]); }
    finally { setCitySearching(false); }
  };

  const selectCityResult = (result) => {
    setGpsDraft({ lat: result.lat, lng: result.lng });
    setFlyTo({ lat: result.lat, lng: result.lng });
    setCitySearch(result.label.split(',')[0]);
    setCityResults([]);
  };

  const saveGps = async () => {
    setGpsSaving(true);
    try {
      await updateDive(id, { latitude: gpsDraft.lat, longitude: gpsDraft.lng, gps_source: 'manual' });
      setDive(d => ({ ...d, latitude: gpsDraft.lat, longitude: gpsDraft.lng, gps_source: 'manual' }));
      setGpsPicker(false);
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.error || e.message));
    } finally {
      setGpsSaving(false);
    }
  };

  const fmtDuration = (sec) => {
    if (!sec) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.round((sec % 3600) / 60);
    return h > 0 ? `${h}u ${m}m` : `${m}m`;
  };

  const [activeTab, setActiveTab] = useState('profiel');

  if (loading) return <div className="loading">Duik laden…</div>;
  if (!dive)   return <div className="error">Duik niet gevonden.</div>;

  const tabs = [
    { key: 'profiel',  label: '📈 Profiel',  show: true },
    { key: 'gegevens', label: '📋 Gegevens', show: true },
    { key: 'fotos',    label: `📷 Foto's${photos.length > 0 ? ` (${photos.length})` : ''}`, show: true },
    { key: 'stempels', label: `🔏 Stempels${stamps.length > 0 ? ` (${stamps.length})` : ''}`, show: true },
  ];

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            <button onClick={() => navigate('/dives')} className="btn btn-outline" style={{ fontSize: 12 }}>← Terug</button>
            <button
              onClick={() => neighbors.prev && navigate(`/dives/${neighbors.prev.id}`)}
              disabled={!neighbors.prev}
              title={neighbors.prev ? `Duik #${neighbors.prev.num}` : 'Geen vorige duik'}
              className="btn btn-outline"
              style={{ fontSize: 12, opacity: neighbors.prev ? 1 : 0.35 }}>‹</button>
            <button
              onClick={() => neighbors.next && navigate(`/dives/${neighbors.next.id}`)}
              disabled={!neighbors.next}
              title={neighbors.next ? `Duik #${neighbors.next.num}` : 'Geen volgende duik'}
              className="btn btn-outline"
              style={{ fontSize: 12, opacity: neighbors.next ? 1 : 0.35 }}>›</button>
          </div>
          <h1 className="page-title">Duik #{dive.dive_number}</h1>
          <p className="page-subtitle">{dive.dive_date} · {dive.dive_time ? dive.dive_time.substring(0,5) : ''} · {dive.location_name || dive.site_name || 'Locatie onbekend'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => { setEditing(!editing); setActiveTab('gegevens'); }}>✏️ Bewerken</button>
          <button className="btn btn-danger"  onClick={remove}>🗑️ Verwijderen</button>
        </div>
      </div>

      {/* Kaart — altijd zichtbaar (map als GPS beschikbaar, anders placeholder) */}
      {(() => {
        const lat = parseFloat(dive.latitude  ?? dive.site_latitude  ?? '');
        const lon = parseFloat(dive.longitude ?? dive.site_longitude ?? '');
        const hasGps = !isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0;
        const latExit  = parseFloat(dive.latitude_exit  ?? '');
        const lonExit  = parseFloat(dive.longitude_exit ?? '');
        const hasExit  = !isNaN(latExit) && !isNaN(lonExit) && latExit !== 0 && lonExit !== 0;

        const gpsBadge = hasGps
          ? dive.gps_source === 'manual'
            ? { label: '✋ Manueel · bij benadering', bg: '#fff7ed', color: '#c2410c' }
            : dive.gps_source === 'photo'
            ? { label: '📷 Foto GPS · bij benadering', bg: '#f5f3ff', color: '#6d28d9' }
            : { label: '🎯 Oceanic GPS · exact',       bg: '#f0fdf4', color: '#15803d' }
          : null;

        return (
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            {hasGps ? (
              <div style={{ position: 'relative', height: 200 }}>
                <MapContainer
                  key={`${lat}-${lon}`}
                  center={[lat, lon]}
                  zoom={diveMapZoom}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={true}
                  scrollWheelZoom={false}
                >
                  {diveMapLayer === 'satellite' ? (
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      attribution="© Esri"
                    />
                  ) : (
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="© OpenStreetMap contributors"
                    />
                  )}
                  <ZoomTracker onZoomEnd={z => { setDiveMapZoom(z); saveDiveMapPref(diveMapLayer, z); }} />
                  {/* Automatisch inzoomen zodat beide punten zichtbaar zijn */}
                  {hasExit && <BoundsFitter bounds={[[lat, lon], [latExit, lonExit]]} />}
                  {/* Instappunt (blauw) */}
                  <Marker position={[lat, lon]} />
                  {/* Rode lijn + uitstappunt */}
                  {hasExit && (
                    <>
                      <Polyline
                        positions={[[lat, lon], [latExit, lonExit]]}
                        pathOptions={{ color: '#ef4444', weight: 2.5, dashArray: '6 4', opacity: 0.9 }}
                      />
                      <CircleMarker
                        center={[latExit, lonExit]}
                        radius={7}
                        pathOptions={{ color: '#fff', weight: 2, fillColor: '#ef4444', fillOpacity: 1 }}
                      />
                    </>
                  )}
                </MapContainer>
                {/* Kaart / Satelliet toggle */}
                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1000, display: 'flex', gap: 4 }}>
                  {['satellite', 'map'].map(layer => (
                    <button key={layer}
                      onClick={() => { setDiveMapLayer(layer); saveDiveMapPref(layer, diveMapZoom); }}
                      style={{
                        padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        borderRadius: 6, border: 'none',
                        background: diveMapLayer === layer ? 'var(--blue)' : 'rgba(255,255,255,.85)',
                        color:      diveMapLayer === layer ? '#fff' : '#333',
                        boxShadow: '0 1px 4px rgba(0,0,0,.25)',
                      }}>
                      {layer === 'satellite' ? '🛰️' : '🗺️'}
                    </button>
                  ))}
                </div>
                {gpsBadge && (
                  <div style={{
                    position: 'absolute', bottom: 8, right: 8, zIndex: 1000,
                    background: gpsBadge.bg, color: gpsBadge.color,
                    fontSize: 11, fontWeight: 600, padding: '3px 10px',
                    borderRadius: 99, boxShadow: '0 1px 4px rgba(0,0,0,.2)',
                    pointerEvents: 'none',
                  }}>
                    {gpsBadge.label}
                  </div>
                )}
                {(dive.gps_source === 'manual' || dive.gps_source === 'photo') && (
                  <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000, display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => {
                        const clat = parseFloat(dive.latitude);
                        const clng = parseFloat(dive.longitude);
                        setGpsDraft({ lat: isNaN(clat) ? 20 : clat, lng: isNaN(clng) ? 10 : clng });
                        setGpsPicker(true);
                      }}
                      style={{
                        background: 'rgba(255,255,255,.92)', color: '#c2410c',
                        border: 'none', borderRadius: 8, padding: '4px 11px',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(0,0,0,.25)',
                      }}>
                      ✏️ Aanpassen
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('GPS-locatie verwijderen?')) return;
                        try {
                          await updateDive(id, { latitude: null, longitude: null, gps_source: null });
                          setDive(d => ({ ...d, latitude: null, longitude: null, gps_source: null }));
                        } catch (e) { alert('Verwijderen mislukt: ' + e.message); }
                      }}
                      style={{
                        background: 'rgba(255,255,255,.92)', color: '#dc2626',
                        border: 'none', borderRadius: 8, padding: '4px 11px',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(0,0,0,.25)',
                      }}>
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>📍</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Geen GPS-coördinaten</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {dive.location_name || dive.site_name
                      ? `${dive.location_name || dive.site_name} — geen exacte positie beschikbaar`
                      : 'Geen locatiedata beschikbaar voor deze duik'}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setGpsDraft({ lat: 20, lng: 10 });
                    setGpsPicker(true);
                  }}
                  style={{
                    background: 'var(--blue-light)', color: 'var(--blue)',
                    border: 'none', borderRadius: 8, padding: '6px 14px',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                  }}>
                  📍 Toevoegen
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* GPS-uit-foto banner */}
      {gpsFromPhoto && (
        <div style={{
          background: '#f5f3ff', border: '1.5px solid #c4b5fd',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>📷</span>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#6d28d9' }}>GPS gevonden in foto</div>
            <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 2 }}>
              {gpsFromPhoto.lat.toFixed(5)}, {gpsFromPhoto.lng.toFixed(5)} · Wil je dit gebruiken als locatie voor deze duik?
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={async () => {
                try {
                  await updateDive(id, { latitude: gpsFromPhoto.lat, longitude: gpsFromPhoto.lng, gps_source: 'photo' });
                  setDive(d => ({ ...d, latitude: gpsFromPhoto.lat, longitude: gpsFromPhoto.lng, gps_source: 'photo' }));
                } catch (e) { alert('Opslaan mislukt: ' + e.message); }
                setGpsFromPhoto(null);
              }}
              style={{ padding: '5px 14px', borderRadius: 7, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              ✓ Ja, gebruiken
            </button>
            <button onClick={() => setGpsFromPhoto(null)}
              style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #c4b5fd', background: 'none', cursor: 'pointer', fontSize: 12, color: '#7c3aed' }}>
              Negeren
            </button>
          </div>
        </div>
      )}

      {/* Tab balk met scroll-indicator */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
      <div style={{
        display: 'flex', gap: 2,
        borderBottom: '2px solid var(--border)',
        overflowX: 'auto', scrollbarWidth: 'none',
      }}>
        {tabs.filter(t => t.show).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? 'var(--blue)' : 'var(--text-muted)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--blue)' : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'color .15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Scroll-indicator rechts */}
      <div style={{
        position: 'absolute', right: 0, top: 0, bottom: 2, width: 36, pointerEvents: 'none',
        background: 'linear-gradient(to right, transparent, var(--card) 80%)',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 4,
      }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', opacity: 0.6 }}>›</span>
      </div>
      </div>

      {/* Tab: Profiel */}
      {activeTab === 'profiel' && (
        <>
          {profile.length > 0 ? (
            <>
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div className="card-title" style={{ marginBottom: 0 }}>Duikprofiel</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {SERIES.map(s => (
                      <button key={s.key} onClick={() => toggleSeries(s.key)}
                        style={{
                          padding: '4px 10px', borderRadius: 99, fontSize: 11, cursor: 'pointer',
                          border: `2px solid ${s.color}`,
                          background: visible[s.key] ? s.color : 'transparent',
                          color: visible[s.key] ? '#fff' : s.color,
                          fontWeight: 600, transition: 'all .15s',
                        }}>
                        {s.label}
                      </button>
                    ))}
                    <button onClick={() => setChartFullscreen(true)}
                      title="Volledig scherm"
                      style={{
                        padding: '4px 10px', borderRadius: 99, fontSize: 14, cursor: 'pointer',
                        border: '2px solid var(--border)', background: 'transparent',
                        color: 'var(--text-muted)', transition: 'all .15s', lineHeight: 1,
                      }}>
                      ⛶
                    </button>
                  </div>
                </div>
                <DiveChart profile={profile} visible={visible} height={window.innerWidth >= 768 ? 380 : 240} />
              </div>

              {/* Fullscreen overlay */}
              {chartFullscreen && (
                <div onClick={() => setChartFullscreen(false)} className="chart-fullscreen-overlay">
                  <div onClick={e => e.stopPropagation()} className="chart-fullscreen-toolbar">
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {SERIES.map(s => (
                        <button key={s.key} onClick={() => toggleSeries(s.key)}
                          style={{
                            padding: '3px 9px', borderRadius: 99, fontSize: 11, cursor: 'pointer',
                            border: `2px solid ${s.color}`,
                            background: visible[s.key] ? s.color : 'transparent',
                            color: visible[s.key] ? '#fff' : s.color,
                            fontWeight: 600,
                          }}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setChartFullscreen(false)}
                      style={{
                        background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
                        borderRadius: 99, width: 30, height: 30, fontSize: 15, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                      ✕
                    </button>
                  </div>
                  <div className="chart-fullscreen-body" onClick={e => e.stopPropagation()}>
                    <DiveChart profile={profile} visible={visible} height="100%" dark />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Geen profieldata beschikbaar voor deze duik.
            </div>
          )}
        </>
      )}

      {/* Tab: Gegevens */}
      {activeTab === 'gegevens' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
          {/* Technische data */}
          <div className="card">
            <div className="card-title">Technische gegevens</div>
            <InfoRow label="Max diepte"     value={`${dive.max_depth_m ?? '—'} m`} />
            <InfoRow label="Gem. diepte"    value={`${dive.avg_depth_m ?? '—'} m`} />
            <InfoRow label="Duiktijd"       value={fmtDuration(dive.duration_sec)} />
            <InfoRow label="Temp (min/max)" value={`${dive.temp_min_c ?? '—'}°C / ${dive.temp_max_c ?? '—'}°C`} />
            <InfoRow label="Gasmix"         value={dive.gas_o2_percent > 21.5 ? `EAN${Math.round(dive.gas_o2_percent)}` : 'Lucht'} />
            <InfoRow label="Flesdruk start" value={dive.tank_pressure_begin_bar ? `${dive.tank_pressure_begin_bar} bar` : '—'} />
            <InfoRow label="Flesdruk einde" value={dive.tank_pressure_end_bar  ? `${dive.tank_pressure_end_bar} bar`  : '—'} />
            <InfoRow label="CNS einde"      value={dive.cns_end_percent != null ? `${dive.cns_end_percent}%` : '—'} />
            <InfoRow label="Decomodel"      value={dive.deco_model || '—'} />
            <InfoRow label="Computer"       value={dive.computer_name || '—'} />
            <SacRow dive={dive} />
          </div>

          {/* Bewerkbaar veld */}
          <div className="card">
            <div className="card-title">Duikgegevens {editing && <span style={{ fontSize: 12, color: 'var(--ocean)', marginLeft: 8 }}>(bewerkmodus)</span>}</div>

            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Field label="Locatie" name="location_name" form={form} setForm={setForm} />

                <BuddyField form={form} setForm={setForm} buddyList={buddyList.length > 0 ? buddyList : suggestions.buddies} />

                <SelectField
                  label="Zichtbaarheid"
                  name="visibility_m"
                  form={form} setForm={setForm}
                  options={[
                    { value: '', label: '– kies –' },
                    ...(visibilityList.length > 0
                      ? visibilityList.map(v => ({ value: String(v.value_m), label: v.label }))
                      : [
                          { value: '1',  label: '< 1 m' },
                          { value: '2',  label: '2 m' },
                          { value: '3',  label: '3 m' },
                          { value: '5',  label: '5 m' },
                          { value: '8',  label: '8 m' },
                          { value: '10', label: '10 m' },
                          { value: '15', label: '15 m' },
                          { value: '20', label: '20 m' },
                          { value: '25', label: '25 m' },
                          { value: '30', label: '30 m+' },
                        ]
                    ),
                  ]}
                />

                <SelectField
                  label="Water"
                  name="water_type"
                  form={form} setForm={setForm}
                  options={[
                    { value: '',     label: '– kies –' },
                    { value: 'zout', label: 'Zout' },
                    { value: 'brak', label: 'Brak' },
                    { value: 'zoet', label: 'Zoet' },
                  ]}
                />

                <MultiSelectField label="Pak" field="suit_type" form={form} setForm={setForm}
                  options={lookups.pak?.length ? lookups.pak : ['Droogpak', '7mm', '5mm', '3mm', 'Korte broek', 'Geen']} />

                <Field label="Lood (kg)"     name="weight_kg"    form={form} setForm={setForm} type="number" />
                <Field label="Flesvolume (L)" name="tank_volume_l" form={form} setForm={setForm} type="number" />

                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Score</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[1,2,3,4,5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm({ ...form, rating: form.rating == n ? '' : n })}
                        style={{
                          fontSize: 22, background: 'none', border: 'none', cursor: 'pointer',
                          color: n <= (form.rating || 0) ? '#f59e0b' : 'var(--border)',
                          padding: '0 2px', lineHeight: 1,
                        }}
                      >★</button>
                    ))}
                    {form.rating > 0 && (
                      <button type="button" onClick={() => setForm({ ...form, rating: '' })}
                        style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}>
                        wissen
                      </button>
                    )}
                  </div>
                </div>

                <MultiSelectField label="Problemen"  field="problems"  form={form} setForm={setForm} options={lookups.problemen  || []} />
                <MultiSelectField label="Soort duik" field="dive_type" form={form} setForm={setForm} options={lookups.soort      || []} />
                <MultiSelectField label="Reis"       field="trip"      form={form} setForm={setForm} options={lookups.reis       || []} />
                <MultiSelectField label="Thema"      field="theme"     form={form} setForm={setForm} options={lookups.thema      || []} />

                <SelectField
                  label="Soort fles"
                  name="tank_type"
                  form={form} setForm={setForm}
                  options={[{ value: '', label: '– kies –' }, ...(lookups.soort_fles || []).map(v => ({ value: v, label: v }))]}
                />

                <SelectField
                  label="Inspanning"
                  name="exertion"
                  form={form} setForm={setForm}
                  options={[{ value: '', label: '– kies –' }, ...(lookups.inspanning || []).map(v => ({ value: v, label: v }))]}
                />

                {/* Algemeen comfort */}
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Algemeen comfort</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button"
                        onClick={() => setForm({ ...form, comfort_rating: form.comfort_rating == n ? '' : n })}
                        style={{ fontSize: 22, background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1,
                          color: n <= (form.comfort_rating || 0) ? '#22c55e' : 'var(--border)' }}>★</button>
                    ))}
                    {form.comfort_rating > 0 && (
                      <button type="button" onClick={() => setForm({ ...form, comfort_rating: '' })}
                        style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}>wissen</button>
                    )}
                  </div>
                </div>

                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Notities</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, resize: 'vertical' }}
                />
                {saveError && (
                  <div className="error">❌ {saveError}</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" onClick={save}>💾 Opslaan</button>
                  <button className="btn btn-outline" onClick={() => { setEditing(false); setSaveError(null); }}>Annuleren</button>
                </div>
              </div>
            ) : (
              <>
                <InfoRow label="Locatie"       value={dive.location_name || '—'} />
                {(dive.latitude || dive.site_latitude) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-muted)' }}>GPS</span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 99,
                      background: dive.gps_source === 'manual' ? '#fff7ed' : '#f0fdf4',
                      color:      dive.gps_source === 'manual' ? '#c2410c' : '#15803d',
                    }}>
                      {dive.gps_source === 'manual' ? '✋ Manueel · bij benadering'
                      : dive.gps_source === 'photo' ? '📷 Foto GPS · bij benadering'
                      : '🎯 Oceanic GPS · exact'}
                    </span>
                  </div>
                )}
                <BuddyInfoRow buddy={dive.buddy} buddyObjects={buddyObjects} />
                <InfoRow label="Zichtbaarheid" value={dive.visibility_m  ? `${dive.visibility_m} m` : '—'} />
                <InfoRow label="Water"         value={dive.water_type    ? dive.water_type.charAt(0).toUpperCase() + dive.water_type.slice(1) : '—'} />
                <BadgeInfoRow label="Pak"       value={dive.suit_type}     color="#0369a1" bg="#e0f2fe" />
                <InfoRow label="Lood"          value={dive.weight_kg     ? `${dive.weight_kg} kg`   : '—'} />
                <InfoRow label="Flesvolume"    value={dive.tank_volume_l  ? `${dive.tank_volume_l} L`  : '—'} />
                <InfoRow label="Score"         value={dive.rating         ? '⭐'.repeat(dive.rating)          : '—'} />
                <BadgeInfoRow label="Problemen"    value={dive.problems}  color="var(--danger)"   bg="#fef2f2" />
                <BadgeInfoRow label="Soort duik"   value={dive.dive_type} color="var(--blue)"     bg="var(--blue-light)" />
                <BadgeInfoRow label="Reis"         value={dive.trip}      color="#7c3aed"         bg="#f5f3ff" />
                <BadgeInfoRow label="Thema"        value={dive.theme}     color="#d97706"         bg="#fffbeb" />
                <InfoRow label="Soort fles"    value={dive.tank_type      || '—'} />
                <InfoRow label="Inspanning"    value={dive.exertion       || '—'} />
                <InfoRow label="Comfort"       value={dive.comfort_rating ? '🟢'.repeat(dive.comfort_rating) : '—'} />
                <InfoRow label="Notities"      value={(typeof dive.notes === 'string' ? dive.notes : '') || '—'} />
              </>
            )}
          </div>
        </div>
      )}

      {/* Tab: Foto's */}
      {activeTab === 'fotos' && (
        <PhotoGallery
          diveId={id}
          photos={photos}
          setPhotos={setPhotos}
          lightbox={lightbox}
          setLightbox={setLightbox}
          uploading={uploading}
          setUploading={setUploading}
          onGpsSuggested={setGpsFromPhoto}
        />
      )}

      {/* Tab: Stempels */}
      {activeTab === 'stempels' && (
        <StampGallery
          diveId={id}
          stamps={stamps}
          setStamps={setStamps}
        />
      )}

      {/* GPS Picker overlay */}
      {gpsPicker && (
        <div
          onClick={() => setGpsPicker(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,.75)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 14,
              width: '100%', maxWidth: 700, overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,.4)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>📍 GPS-locatie instellen</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  Klik op de kaart of sleep de pin naar de juiste locatie
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Kaart / Satelliet toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: mapLayer === 'map' ? 'var(--blue)' : 'var(--text-muted)', fontWeight: mapLayer === 'map' ? 700 : 400 }}>🗺️ Kaart</span>
                  <div
                    onClick={() => setMapLayer(l => l === 'satellite' ? 'map' : 'satellite')}
                    style={{
                      width: 40, height: 22, borderRadius: 11,
                      background: mapLayer === 'satellite' ? 'var(--blue)' : '#d1d5db',
                      position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 2,
                      left: mapLayer === 'satellite' ? 20 : 2,
                      width: 18, height: 18, borderRadius: 9,
                      background: '#fff', transition: 'left .2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                    }} />
                  </div>
                  <span style={{ fontSize: 11, color: mapLayer === 'satellite' ? 'var(--blue)' : 'var(--text-muted)', fontWeight: mapLayer === 'satellite' ? 700 : 400 }}>🛰️ Satelliet</span>
                </div>
                <button onClick={() => setGpsPicker(false)}
                  style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
              </div>
            </div>

            {/* Stadzoekveld */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', position: 'relative' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="🔍 Zoek stad of locatie…"
                  value={citySearch}
                  onChange={e => setCitySearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchCity(citySearch)}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 8,
                    border: '1.5px solid var(--border)', fontSize: 13,
                    background: 'var(--card)', color: 'var(--text)',
                  }}
                />
                <button
                  onClick={() => searchCity(citySearch)}
                  disabled={citySearching}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: 'none',
                    background: 'var(--blue)', color: '#fff', fontSize: 13,
                    fontWeight: 600, cursor: citySearching ? 'default' : 'pointer',
                    opacity: citySearching ? 0.6 : 1,
                  }}>
                  {citySearching ? '…' : 'Zoek'}
                </button>
              </div>
              {cityResults.length > 0 && (
                <div style={{
                  position: 'absolute', left: 16, right: 16, top: '100%',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 8, zIndex: 10000, boxShadow: '0 8px 24px rgba(0,0,0,.2)',
                  maxHeight: 220, overflowY: 'auto',
                }}>
                  {cityResults.map((r, i) => (
                    <div key={i}
                      onClick={() => selectCityResult(r)}
                      style={{
                        padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                        borderBottom: i < cityResults.length - 1 ? '1px solid var(--border)' : 'none',
                        color: 'var(--text)',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-light)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      📍 {r.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Kaart */}
            <div style={{ height: 380 }}>
              <MapContainer
                key="gps-picker"
                center={[gpsDraft.lat, gpsDraft.lng]}
                zoom={2}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom
              >
                {mapLayer === 'satellite' ? (
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="© Esri"
                  />
                ) : (
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="© OpenStreetMap contributors"
                  />
                )}
                <FlyToLocation target={flyTo} />
                <GpsMarker pos={gpsDraft} setPos={setGpsDraft} />
              </MapContainer>
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                {gpsDraft.lat.toFixed(5)}, {gpsDraft.lng.toFixed(5)}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setGpsPicker(false)}
                  style={{ padding: '7px 16px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
                  Annuleren
                </button>
                <button onClick={saveGps} disabled={gpsSaving}
                  style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: 'var(--blue)', color: '#fff', cursor: gpsSaving ? 'default' : 'pointer', fontSize: 13, fontWeight: 600, opacity: gpsSaving ? 0.6 : 1 }}>
                  {gpsSaving ? 'Opslaan…' : '✓ Opslaan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── BoundsFitter — zoomt de kaart zodat beide GPS-punten zichtbaar zijn ────
function BoundsFitter({ bounds }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [36, 36], maxZoom: 16 });
  }, []);
  return null;
}

// ── GpsMarker — versleepbare pin + kaart-klik handler ──────────────────────
function ZoomTracker({ onZoomEnd }) {
  useMapEvents({ zoomend(e) { onZoomEnd(e.target.getZoom()); } });
  return null;
}

function FlyToLocation({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 12, { duration: 1.2 });
  }, [target, map]);
  return null;
}

function GpsMarker({ pos, setPos }) {
  const markerRef = useRef(null);
  const eventHandlers = useMemo(() => ({
    dragend() {
      const m = markerRef.current;
      if (m) setPos(m.getLatLng());
    },
  }), [setPos]);
  useMapEvents({
    click(e) { setPos(e.latlng); },
  });
  return (
    <Marker
      draggable
      position={[pos.lat, pos.lng]}
      ref={markerRef}
      eventHandlers={eventHandlers}
    />
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function Field({ label, name, form, setForm, type = 'text' }) {
  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{label}</label>
      <input
        type={type}
        value={form[name] ?? ''}
        onChange={e => setForm({ ...form, [name]: e.target.value })}
        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
      />
    </div>
  );
}


// ── CustomTooltip — toont enkel de actieve series, combineert o_* naar één Snelheid-rij ──
function CustomTooltip({ active, payload, label, dark }) {
  if (!active || !payload?.length) return null;

  const bg     = dark ? '#1e293b' : '#fff';
  const border = dark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.08)';
  const txtMain = dark ? '#fff'                 : '#333';
  const txtSub  = dark ? 'rgba(255,255,255,.55)' : '#666';

  const SPEED_HEX = { red: '#ef4444', green: '#22c55e', gray: '#94a3b8', blue: '#3b82f6' };

  let speedVal = null;
  const items = [];

  for (const entry of payload) {
    const { dataKey, value, color } = entry;
    if (value == null) continue;
    if (dataKey === 'diepte')  { items.push({ label: 'Diepte',       val: `${Math.abs(value).toFixed(1)} m`, color }); continue; }
    if (dataKey === 'temp')    { items.push({ label: 'Temperatuur',   val: `${value} °C`,                    color }); continue; }
    if (dataKey === 'ndt')     { items.push({ label: 'NDT',           val: `${value} min`,                   color }); continue; }
    if (dataKey === 'cns')     { items.push({ label: 'CNS',           val: `${value} %`,                     color }); continue; }
    if (dataKey.startsWith('o_') && speedVal == null) { speedVal = value; }
  }

  if (speedVal != null) {
    const dotColor = SPEED_HEX[speedColor(speedVal)] ?? '#94a3b8';
    items.push({ label: 'Snelheid', val: `${speedVal} m/min`, color: dotColor });
  }

  if (!items.length) return null;

  return (
    <div style={{ background: bg, borderRadius: 10, fontSize: 12, padding: '8px 12px', border: `1px solid ${border}`, boxShadow: '0 4px 20px rgba(0,0,0,.3)' }}>
      <div style={{ fontWeight: 600, marginBottom: 5, color: txtMain }}>{label} min</div>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
          <span style={{ color: txtSub }}>{item.label}:</span>
          <span style={{ fontWeight: 600, color: txtMain }}>{item.val}</span>
        </div>
      ))}
    </div>
  );
}

function DiveChart({ profile, visible, height = 240, dark = false }) {
  const gridColor  = dark ? 'rgba(255,255,255,.1)' : 'var(--border)';
  const tickColor  = dark ? 'rgba(255,255,255,.5)' : undefined;
  const tickStyle  = { fontSize: 11, fill: tickColor };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={profile} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="depthGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#0077b6" stopOpacity={0.6} />
            <stop offset="95%" stopColor="#0077b6" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="t" tickFormatter={v => `${v}m`} tick={tickStyle} />
        <YAxis yAxisId="depth" tickFormatter={v => `${Math.abs(v)}m`} tick={tickStyle} domain={['auto', 0]} />
        <YAxis yAxisId="other" orientation="right" tick={tickStyle} />
        <Tooltip content={<CustomTooltip dark={dark} />} />
        {visible.diepte  && <Area yAxisId="depth" type="monotone" dataKey="diepte"  stroke="#0077b6" fill="url(#depthGrad)" strokeWidth={2} dot={false} />}
        {visible.temp    && <Line yAxisId="other" type="monotone" dataKey="temp"    stroke="#f59e0b" strokeWidth={2} dot={false} />}
        {visible.ndt     && <Line yAxisId="other" type="monotone" dataKey="ndt"     stroke="#22c55e" strokeWidth={2} dot={false} />}
        {visible.cns     && <Line yAxisId="other" type="monotone" dataKey="cns"     stroke="#ef4444" strokeWidth={2} dot={false} />}
        {/* 4 gekleurde lijnen: rood >9, groen 0-9, grijs stabiel, blauw afdalen */}
        {visible.opstijg && [
          { key: 'o_red',   color: '#ef4444' },
          { key: 'o_green', color: '#22c55e' },
          { key: 'o_gray',  color: '#94a3b8' },
          { key: 'o_blue',  color: '#3b82f6' },
        ].map(({ key, color }) => (
          <Line key={key} yAxisId="other" type="monotone" dataKey={key}
            stroke={color} strokeWidth={2} dot={false} connectNulls={false} />
        ))}
        {visible.opstijg && (
          <ReferenceLine yAxisId="other" y={9}
            stroke="#ef4444" strokeDasharray="5 3" strokeWidth={1}
            label={{ value: '9 m/min', position: 'insideTopRight', fontSize: 10, fill: '#ef4444' }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function SacRow({ dive }) {
  const begin   = parseFloat(dive.tank_pressure_begin_bar);
  const end     = parseFloat(dive.tank_pressure_end_bar);
  const vol     = parseFloat(dive.tank_volume_l);
  const depth   = parseFloat(dive.avg_depth_m);
  const minutes = dive.duration_sec / 60;

  if (!begin || !end || !vol || !depth || !minutes || begin <= end) return null;

  const gasUsedL   = (begin - end) * vol;
  const atmPressure = depth / 10 + 1;
  const sac         = gasUsedL / minutes / atmPressure;

  let kwaliteit = '';
  let kleur = 'var(--text-muted)';
  if (sac < 15)       { kwaliteit = '🟢 Uitstekend';  kleur = '#16a34a'; }
  else if (sac < 20)  { kwaliteit = '🟡 Goed';         kleur = '#d97706'; }
  else if (sac < 25)  { kwaliteit = '🟠 Gemiddeld';    kleur = '#ea580c'; }
  else                { kwaliteit = '🔴 Hoog';          kleur = '#dc2626'; }

  return (
    <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
        <span style={{ color: 'var(--text-muted)' }}>SAC-rate</span>
        <span style={{ fontWeight: 700, color: kleur }}>
          {sac.toFixed(1)} L/min &nbsp;{kwaliteit}
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
        ({begin - end} bar × {vol}L ÷ {minutes.toFixed(0)}min ÷ {atmPressure.toFixed(1)}atm)
      </div>
    </div>
  );
}

function SelectField({ label, name, form, setForm, options }) {
  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{label}</label>
      <select
        value={form[name] ?? ''}
        onChange={e => setForm({ ...form, [name]: e.target.value })}
        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, background: 'var(--card)' }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function SpeciesTags({ species, minConfidence = 0 }) {
  if (!species?.length) return null;
  const filtered = species.filter(s => s.confidence == null || s.confidence >= minConfidence);
  if (!filtered.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, padding: '4px 6px' }}>
      {filtered.map((s, i) => (
        <span key={i} title={`${s.latin_name || ''}${s.confidence ? ` · ${s.confidence}%` : ''}${s.count > 1 ? ` · ×${s.count}` : ''}`}
          style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 99,
            background: 'var(--blue-light)', color: 'var(--blue)',
            fontWeight: 600, cursor: 'default', whiteSpace: 'nowrap',
          }}>
          🐟 {s.dutch_name}
        </span>
      ))}
    </div>
  );
}

function PhotoGallery({ diveId, photos, setPhotos, lightbox, setLightbox, uploading, setUploading, onGpsSuggested }) {
  const fileRef = useRef();
  const [dragOver, setDragOver]             = useState(false);
  const [videoPickerOpen, setVideoPickerOpen] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null); // photo.id
  const [captionDraft, setCaptionDraft]     = useState('');

  // species: { [photoId]: [...] }
  const [photoSpecies, setPhotoSpecies]   = useState({});
  const [identifying, setIdentifying]     = useState(null); // photoId bezig
  const [minConfidence, setMinConfidence] = useState(60);

  // Soortenbeheerpaneel (lightbox)
  const [manualInput,    setManualInput]    = useState('');
  const [manualAdding,   setManualAdding]   = useState(false);
  const [exampleData,    setExampleData]    = useState(null);  // { latin_name, dutch_name, imageUrl, wikiUrl }
  const [exampleLoading, setExampleLoading] = useState(false);
  const [exampleZoomed,  setExampleZoomed]  = useState(false); // volledig scherm voor referentiefoto

  useEffect(() => {
    getAppSetting('species_min_confidence').then(r => {
      setMinConfidence(parseInt(r.value) || 60);
    }).catch(() => {});
  }, []);

  // Laad bestaande herkenningen + poll zolang er pending foto's zijn
  const loadSpecies = useCallback(async (photoList) => {
    for (const p of photoList) {
      if (p.species_status === 'done' || p.species_status === null) {
        if (p.species_status === 'done') {
          try {
            const s = await getPhotoSpecies(diveId, p.id);
            setPhotoSpecies(prev => ({ ...prev, [p.id]: s }));
          } catch {}
        }
      }
    }
  }, [diveId]);

  useEffect(() => { loadSpecies(photos); }, [photos.length]);

  // Poll elke 8 seconden zolang er pending/processing foto's zijn
  useEffect(() => {
    const hasPending = photos.some(p => p.species_status === 'pending' || p.species_status === 'processing');
    if (!hasPending) return;
    const timer = setInterval(async () => {
      try {
        const updated = await getDivePhotos(diveId);
        setPhotos(updated);
        // Laad species voor foto's die nu 'done' zijn
        const nowDone = updated.filter(p => p.species_status === 'done' && !photoSpecies[p.id]);
        for (const p of nowDone) {
          const s = await getPhotoSpecies(diveId, p.id);
          setPhotoSpecies(prev => ({ ...prev, [p.id]: s }));
        }
      } catch {}
    }, 8000);
    return () => clearInterval(timer);
  }, [photos.map(p => p.species_status).join(',')]);

  // Manuele heranalyse (bijv. vanuit lightbox)
  const handleIdentify = async (photo, e) => {
    e?.stopPropagation();
    setIdentifying(photo.id);
    try {
      const result = await identifyPhoto(diveId, photo.id);
      setPhotoSpecies(prev => ({ ...prev, [photo.id]: result }));
    } catch (err) {
      alert('Herkenning mislukt: ' + err.message);
    } finally {
      setIdentifying(null);
    }
  };

  // ── Slideshow state ──────────────────────────────────────────────────────
  const [slideshow, setSlideshow]           = useState(false);
  const [slideIdx, setSlideIdx]             = useState(0);
  const [slidePlaying, setSlidePlaying]     = useState(true);
  const [slideFs, setSlideFs]               = useState(false);
  const slideContainerRef                   = useRef();
  const slideTimerRef                       = useRef();

  // ── Soortenbeheerpaneel functies ──────────────────────────────────────────
  const handleDeleteSpecies = async (photoId, latinName, dutchName) => {
    try {
      await deletePhotoSpecies(diveId, photoId, { latin_name: latinName, dutch_name: dutchName });
      const updated = await getPhotoSpecies(diveId, photoId);
      setPhotoSpecies(prev => ({ ...prev, [photoId]: updated }));
    } catch (e) { alert('Verwijderen mislukt: ' + e.message); }
  };

  const handleUpdateDutchName = async (photoId, latinName, newDutchName) => {
    try {
      await updateSpeciesDutchName(diveId, photoId, { latin_name: latinName, dutch_name: newDutchName });
      const updated = await getPhotoSpecies(diveId, photoId);
      setPhotoSpecies(prev => ({ ...prev, [photoId]: updated }));
    } catch (e) { alert('Naam bijwerken mislukt: ' + e.message); }
  };

  const fetchExampleImage = async (latinName) => {
    if (!latinName) return;
    setExampleLoading(true);
    setExampleData(null);
    try {
      const r = await fetch(`https://api.inaturalist.org/v1/taxa?q=${encodeURIComponent(latinName)}&per_page=1&locale=nl`);
      const data = await r.json();
      const taxon = data.results?.[0];
      setExampleData({
        latin_name: latinName,
        dutch_name: taxon?.preferred_common_name || taxon?.name || latinName,
        imageUrl:   taxon?.default_photo?.medium_url || null,
        wikiUrl:    taxon?.wikipedia_url || null,
      });
    } catch { setExampleData({ latin_name: latinName, imageUrl: null }); }
    finally { setExampleLoading(false); }
  };

  const handleAddManual = async (photoId) => {
    const latin = manualInput.trim();
    if (!latin) return;
    setManualAdding(true);
    try {
      // Probeer Nederlandse naam op te halen via iNaturalist
      let dutch = latin;
      try {
        const r = await fetch(`https://api.inaturalist.org/v1/taxa?q=${encodeURIComponent(latin)}&per_page=1&locale=nl`);
        const data = await r.json();
        const taxon = data.results?.[0];
        if (taxon?.preferred_common_name) dutch = taxon.preferred_common_name;
        else if (taxon?.name)             dutch = taxon.name;
      } catch {}
      await addManualSpecies(diveId, photoId, { latin_name: latin, dutch_name: dutch });
      const updated = await getPhotoSpecies(diveId, photoId);
      setPhotoSpecies(prev => ({ ...prev, [photoId]: updated }));
      setManualInput('');
    } catch (e) { alert('Toevoegen mislukt: ' + e.message); }
    finally { setManualAdding(false); }
  };

  const openSlideshow = () => {
    if (photos.length === 0) return;
    setSlideIdx(0);
    setSlidePlaying(true);
    setSlideshow(true);
  };

  const closeSlideshow = () => {
    setSlideshow(false);
    setSlidePlaying(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    setSlideFs(false);
  };

  const slidePrev = () => setSlideIdx(i => (i - 1 + photos.length) % photos.length);
  const slideNext = () => setSlideIdx(i => (i + 1) % photos.length);

  const toggleSlideFs = () => {
    if (!document.fullscreenElement) {
      slideContainerRef.current?.requestFullscreen().then(() => setSlideFs(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setSlideFs(false)).catch(() => {});
    }
  };

  // Auto-advance
  useEffect(() => {
    if (slideshow && slidePlaying && photos.length > 1) {
      slideTimerRef.current = setInterval(() => setSlideIdx(i => (i + 1) % photos.length), 4000);
    }
    return () => clearInterval(slideTimerRef.current);
  }, [slideshow, slidePlaying, photos.length]);

  // Keyboard navigatie in slideshow
  useEffect(() => {
    if (!slideshow) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') slideNext();
      if (e.key === 'ArrowLeft')  slidePrev();
      if (e.key === 'Escape')     closeSlideshow();
      if (e.key === ' ')          { e.preventDefault(); setSlidePlaying(p => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [slideshow]);

  // Esc sluit zoom-overlay referentiefoto
  useEffect(() => {
    if (!exampleZoomed) return;
    const handler = (e) => { if (e.key === 'Escape') setExampleZoomed(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [exampleZoomed]);

  const startEditCaption = (photo, e) => {
    e.stopPropagation();
    setEditingCaption(photo.id);
    setCaptionDraft(photo.caption || '');
  };

  const saveCaption = async (photo) => {
    await updatePhotoCaption(diveId, photo.id, captionDraft);
    setPhotos(ps => ps.map(p => p.id === photo.id ? { ...p, caption: captionDraft } : p));
    setEditingCaption(null);
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const result = await uploadDivePhotos(diveId, [...files]);
      const updated = await getDivePhotos(diveId);
      setPhotos(updated);
      // GPS gevonden in EXIF → bubble up naar DiveDetail
      if (result?.suggestedGps && onGpsSuggested) {
        onGpsSuggested(result.suggestedGps);
      }
    } catch (err) {
      alert('Upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photo) => {
    if (!window.confirm('Foto verwijderen?')) return;
    await deleteDivePhoto(diveId, photo.id);
    setPhotos(p => p.filter(x => x.id !== photo.id));
    if (lightbox?.id === photo.id) setLightbox(null);
  };

  const thumbUrl  = (f) => `/uploads/photos/${f}_thumb.jpg`;
  const mediumUrl = (f) => `/uploads/photos/${f}_medium.jpg`;

  return (
    <div
      className="card"
      style={{ position: 'relative' }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={e => {
        // alleen resetten als we de card écht verlaten (niet naar een child)
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
      }}
      onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
    >
      {/* Drop-overlay als er al foto's zijn en gebruiker sleept */}
      {dragOver && photos.length > 0 && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10, borderRadius: 12,
          background: 'var(--blue-light)', border: '2px dashed var(--blue)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 8, pointerEvents: 'none',
        }}>
          <div style={{ fontSize: 36 }}>📷</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--blue)' }}>Loslaten om toe te voegen</div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>📷 Foto's ({photos.length})</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {photos.length > 0 && (
            <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }}
              onClick={openSlideshow}>
              ▶ DiaShow
            </button>
          )}
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }}
            onClick={() => setVideoPickerOpen(true)}>
            🎬 Frames uit video
          </button>
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }}
            onClick={() => fileRef.current?.click()}>
            + Foto's toevoegen
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)} />
      </div>

      {videoPickerOpen && (
        <VideoFramePicker
          onClose={() => setVideoPickerOpen(false)}
          onUpload={handleFiles}
        />
      )}

      {/* Drop-zone als er nog geen foto's zijn */}
      {photos.length === 0 && (
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? 'var(--blue)' : 'var(--border)'}`,
            borderRadius: 12, padding: '32px 16px', textAlign: 'center',
            cursor: 'pointer', background: dragOver ? 'var(--blue-light)' : 'transparent',
            transition: 'all .15s',
          }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {uploading ? 'Uploaden…' : 'Sleep foto\'s hierheen of klik om te kiezen'}
          </div>
        </div>
      )}

      {/* Galerij grid */}
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
          {photos.map(photo => (
            <div key={photo.id} style={{ borderRadius: 8, overflow: 'hidden', background: 'var(--surface)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ position: 'relative', aspectRatio: '1', cursor: 'pointer' }}>
                <img
                  src={thumbUrl(photo.filename)}
                  alt={photo.caption || ''}
                  onClick={() => setLightbox(photo)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <button
                  onClick={() => handleDelete(photo)}
                  style={{
                    position: 'absolute', top: 4, right: 4,
                    background: 'rgba(0,0,0,.55)', border: 'none', color: '#fff',
                    borderRadius: 99, width: 22, height: 22, fontSize: 11,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>✕</button>
                {/* Pending indicator */}
                {(photo.species_status === 'pending' || photo.species_status === 'processing') && (
                  <div title="Soorten worden herkend…"
                    style={{
                      position: 'absolute', bottom: 4, right: 4,
                      background: 'rgba(0,0,0,.55)', borderRadius: 99,
                      width: 22, height: 22, fontSize: 11,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>⏳</div>
                )}
              </div>
              {/* Species tags */}
              {photoSpecies[photo.id]?.length > 0 && <SpeciesTags species={photoSpecies[photo.id]} minConfidence={minConfidence} />}
              {/* Caption */}
              {editingCaption === photo.id ? (
                <div style={{ padding: '4px 6px', display: 'flex', gap: 4 }}>
                  <input
                    autoFocus
                    value={captionDraft}
                    onChange={e => setCaptionDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveCaption(photo); if (e.key === 'Escape') setEditingCaption(null); }}
                    placeholder="Beschrijving…"
                    style={{ flex: 1, fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid var(--blue)', outline: 'none', minWidth: 0 }}
                  />
                  <button onClick={() => saveCaption(photo)} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'var(--blue)', color: '#fff', border: 'none', cursor: 'pointer' }}>✓</button>
                </div>
              ) : (
                <div
                  onClick={e => startEditCaption(photo, e)}
                  style={{ padding: '4px 6px', fontSize: 11, color: photo.caption ? 'var(--text)' : 'var(--text-muted)', cursor: 'text', minHeight: 22, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={photo.caption || 'Klik om beschrijving toe te voegen'}
                >
                  {photo.caption || '+ beschrijving'}
                </div>
              )}
            </div>
          ))}
          {/* Extra upload tegel */}
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              aspectRatio: '1', borderRadius: 8, border: '2px dashed var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--text-muted)', fontSize: 24,
              background: 'transparent',
            }}>
            {uploading ? '⏳' : '+'}
          </div>
        </div>
      )}

      {/* ── DiaShow overlay ────────────────────────────────────────────────── */}
      {slideshow && photos.length > 0 && (
        <div
          ref={slideContainerRef}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: '#000',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {/* Foto */}
          <img
            src={mediumUrl(photos[slideIdx].filename)}
            alt={photos[slideIdx].caption || ''}
            style={{
              maxWidth: '100%', maxHeight: 'calc(100vh - 120px)',
              objectFit: 'contain', borderRadius: 4,
              transition: 'opacity .3s',
            }}
          />

          {/* Caption */}
          {photos[slideIdx].caption && (
            <div style={{
              position: 'absolute', bottom: 70, left: 0, right: 0,
              textAlign: 'center', color: '#fff', fontSize: 14,
              padding: '6px 24px', background: 'rgba(0,0,0,.5)',
            }}>
              {photos[slideIdx].caption}
            </div>
          )}

          {/* Teller */}
          <div style={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,.7)', fontSize: 13,
          }}>
            {slideIdx + 1} / {photos.length}
          </div>

          {/* Navigatie: vorige */}
          {photos.length > 1 && (
            <button onClick={slidePrev} style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
              borderRadius: 99, width: 44, height: 44, fontSize: 22, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>‹</button>
          )}

          {/* Navigatie: volgende */}
          {photos.length > 1 && (
            <button onClick={slideNext} style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
              borderRadius: 99, width: 44, height: 44, fontSize: 22, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>›</button>
          )}

          {/* Onderste balk: play/pause + dots + fullscreen + sluiten */}
          <div style={{
            position: 'absolute', bottom: 16, left: 0, right: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          }}>
            {/* Play / Pause */}
            {photos.length > 1 && (
              <button onClick={() => setSlidePlaying(p => !p)} style={{
                background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
                borderRadius: 99, width: 36, height: 36, fontSize: 15, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {slidePlaying ? '⏸' : '▶'}
              </button>
            )}

            {/* Dots */}
            {photos.length <= 12 && photos.map((_, i) => (
              <div key={i} onClick={() => setSlideIdx(i)} style={{
                width: i === slideIdx ? 10 : 7, height: i === slideIdx ? 10 : 7,
                borderRadius: 99, cursor: 'pointer',
                background: i === slideIdx ? '#fff' : 'rgba(255,255,255,.35)',
                transition: 'all .2s',
              }} />
            ))}

            {/* Fullscreen */}
            <button onClick={toggleSlideFs} title="Volledig scherm" style={{
              background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
              borderRadius: 99, width: 36, height: 36, fontSize: 15, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {slideFs ? '↙' : '⤢'}
            </button>

            {/* Sluiten */}
            <button onClick={closeSlideshow} title="Sluiten (Esc)" style={{
              background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
              borderRadius: 99, width: 36, height: 36, fontSize: 16, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>✕</button>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => { setLightbox(null); setExampleData(null); setManualInput(''); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,.93)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>

          {/* Foto */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 16px 8px', minHeight: 0, width: '100%' }}>
            <img src={mediumUrl(lightbox.filename)} alt={lightbox.caption || ''}
              style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, objectFit: 'contain' }}
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* Caption */}
          {lightbox.caption && (
            <div onClick={e => e.stopPropagation()}
              style={{ color: 'rgba(255,255,255,.8)', fontSize: 13, textAlign: 'center', maxWidth: 560, padding: '0 16px 6px' }}>
              {lightbox.caption}
            </div>
          )}

          {/* Soortenbeheerpaneel */}
          <div onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 640,
              background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(10px)',
              borderTop: '1px solid rgba(255,255,255,.12)',
              padding: '10px 16px 14px',
              maxHeight: '42vh', overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>

            {/* Soortlijst */}
            {(photoSpecies[lightbox.id] || [])
              .filter(s => s.confidence == null || s.confidence >= minConfidence)
              .map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', background: 'rgba(255,255,255,.08)', borderRadius: 8 }}>
                  <span style={{ flex: 1, fontSize: 13, color: '#fff', minWidth: 0 }}>
                    🐟 <strong>{s.dutch_name}</strong>
                    {s.latin_name && <em style={{ marginLeft: 6, fontSize: 11, opacity: 0.6 }}>{s.latin_name}</em>}
                    {s.confidence && <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.4 }}>{s.confidence}%</span>}
                  </span>
                  <button
                    onClick={() => fetchExampleImage(s.latin_name || s.dutch_name)}
                    title="Toon referentieafbeelding ter vergelijking"
                    style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff', borderRadius: 6, padding: '3px 8px', fontSize: 13, cursor: 'pointer' }}>
                    🔍
                  </button>
                  <button
                    onClick={() => handleDeleteSpecies(lightbox.id, s.latin_name, s.dutch_name)}
                    title="Herkenning verwijderen"
                    style={{ background: 'rgba(239,68,68,.2)', border: 'none', color: '#fca5a5', borderRadius: 6, padding: '3px 8px', fontSize: 13, cursor: 'pointer' }}>
                    🗑️
                  </button>
                </div>
              ))
            }

            {/* Referentieafbeelding */}
            {(exampleLoading || exampleData) && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,.12)', paddingTop: 8, marginTop: 2 }}>
                {exampleLoading ? (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)' }}>⏳ Referentie ophalen via iNaturalist…</div>
                ) : exampleData?.imageUrl ? (
                  <div>
                    {/* Header met naam + sluitknop */}
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div>
                        <span
                          title="Klik om deze Nederlandse naam over te nemen"
                          onClick={() => handleUpdateDutchName(lightbox.id, exampleData.latin_name, exampleData.dutch_name)}
                          style={{ fontSize: 13, color: '#86efac', fontWeight: 600, cursor: 'pointer', borderBottom: '1px dashed rgba(134,239,172,.5)', paddingBottom: 1 }}>
                          {exampleData.dutch_name}
                        </span>
                        <em style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,.5)' }}>{exampleData.latin_name}</em>
                        {exampleData.wikiUrl && (
                          <a href={exampleData.wikiUrl} target="_blank" rel="noreferrer"
                            style={{ marginLeft: 10, fontSize: 11, color: '#60a5fa' }}>Wikipedia →</a>
                        )}
                      </div>
                      <button onClick={() => { setExampleData(null); setExampleZoomed(false); }}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.4)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>
                    </div>
                    {/* Afbeelding — klik om te zoomen */}
                    <div style={{ position: 'relative', cursor: 'zoom-in' }} onClick={() => setExampleZoomed(true)}>
                      <img src={exampleData.imageUrl} alt={exampleData.latin_name}
                        style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                      <div style={{
                        position: 'absolute', bottom: 6, right: 6,
                        background: 'rgba(0,0,0,.55)', borderRadius: 6, padding: '3px 7px',
                        fontSize: 11, color: '#fff', display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        🔍 Klik om te vergroten
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>
                    Geen referentieafbeelding gevonden voor <em>{exampleData?.latin_name}</em>
                  </div>
                )}
              </div>
            )}

            {/* Zoom-overlay voor referentiefoto */}
            {exampleZoomed && exampleData?.imageUrl && (
              <div
                onClick={() => setExampleZoomed(false)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 10001,
                  background: 'rgba(0,0,0,.96)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: 16, cursor: 'zoom-out',
                }}>
                <div style={{ marginBottom: 10, textAlign: 'center' }}>
                  <span
                    title="Klik om deze Nederlandse naam over te nemen"
                    onClick={e => { e.stopPropagation(); handleUpdateDutchName(lightbox.id, exampleData.latin_name, exampleData.dutch_name); }}
                    style={{ color: '#86efac', fontWeight: 600, fontSize: 15, cursor: 'pointer', borderBottom: '1px dashed rgba(134,239,172,.5)', paddingBottom: 1 }}>
                    {exampleData.dutch_name}
                  </span>
                  <em style={{ marginLeft: 8, color: 'rgba(255,255,255,.5)', fontSize: 13 }}>{exampleData.latin_name}</em>
                </div>
                <img src={exampleData.imageUrl} alt={exampleData.latin_name}
                  style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 120px)', borderRadius: 10, objectFit: 'contain' }}
                  onClick={e => e.stopPropagation()}
                />
                <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,.35)' }}>
                  Klik buiten de foto of druk Esc om te sluiten
                </div>
                {exampleData.wikiUrl && (
                  <a href={exampleData.wikiUrl} target="_blank" rel="noreferrer"
                    style={{ marginTop: 6, fontSize: 12, color: '#60a5fa' }}>
                    Meer info op Wikipedia →
                  </a>
                )}
                <button onClick={() => setExampleZoomed(false)}
                  style={{
                    position: 'absolute', top: 14, right: 16,
                    background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
                    borderRadius: 99, width: 34, height: 34, fontSize: 17, cursor: 'pointer',
                  }}>✕</button>
              </div>
            )}

            {/* Manuele invoer */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,.12)', paddingTop: 8, marginTop: 2, display: 'flex', gap: 6 }}>
              <input
                value={manualInput}
                onChange={e => setManualInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && manualInput.trim()) handleAddManual(lightbox.id); }}
                placeholder="Latijnse naam invoeren…"
                style={{
                  flex: 1, background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)',
                  borderRadius: 6, padding: '6px 10px', color: '#fff', fontSize: 12, outline: 'none',
                }}
              />
              <button
                onClick={() => handleAddManual(lightbox.id)}
                disabled={!manualInput.trim() || manualAdding}
                style={{
                  padding: '6px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600,
                  background: manualInput.trim() && !manualAdding ? '#3b82f6' : 'rgba(255,255,255,.1)',
                  color: '#fff', cursor: manualInput.trim() && !manualAdding ? 'pointer' : 'default',
                }}>
                {manualAdding ? '⏳' : '+ Voeg toe'}
              </button>
            </div>

            {/* Heranalyse */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,.12)', paddingTop: 8, marginTop: 2 }}>
              <button
                onClick={() => handleIdentify(lightbox)}
                disabled={identifying === lightbox.id}
                style={{
                  width: '100%', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)',
                  color: 'rgba(255,255,255,.75)', borderRadius: 6, padding: '7px', fontSize: 12,
                  cursor: identifying === lightbox.id ? 'wait' : 'pointer',
                }}>
                {identifying === lightbox.id ? '⏳ AI-heranalyse bezig…' : '🤖 AI-heranalyse uitvoeren'}
              </button>
            </div>
          </div>

          {/* Sluitknop */}
          <button onClick={() => { setLightbox(null); setExampleData(null); setManualInput(''); }}
            style={{
              position: 'absolute', top: 12, right: 16,
              background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
              borderRadius: 99, width: 34, height: 34, fontSize: 17, cursor: 'pointer',
            }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── MultiSelectField — herbruikbaar multi-badge veld ─────────────────────────

function MultiSelectField({ label, field, form, setForm, options }) {
  const selected = form[field] ? form[field].split(',').map(s => s.trim()).filter(Boolean) : [];

  const toggle = (val) => {
    const updated = selected.includes(val)
      ? selected.filter(v => v !== val)
      : [...selected, val];
    setForm({ ...form, [field]: updated.join(', ') });
  };

  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{label}</label>
      {options.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {options.map(opt => {
            const active = selected.includes(opt);
            return (
              <button key={opt} type="button" onClick={() => toggle(opt)} style={{
                padding: '4px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                border: `1.5px solid ${active ? 'var(--blue)' : 'var(--border)'}`,
                background: active ? 'var(--blue-light)' : 'transparent',
                color: active ? 'var(--blue)' : 'var(--text-muted)',
                fontWeight: active ? 700 : 400,
                transition: 'all .12s',
              }}>
                {active ? '✓ ' : ''}{opt}
              </button>
            );
          })}
        </div>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Geen opties — voeg toe via Instellingen → Duikgegevens</span>
      )}
    </div>
  );
}

// ── BadgeInfoRow — meerdere waarden als badges ────────────────────────────────

function BadgeInfoRow({ label, value, color = 'var(--blue)', bg = 'var(--blue-light)' }) {
  const list = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13, gap: 8 }}>
      <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      {list.length === 0 ? (
        <span style={{ fontWeight: 500 }}>—</span>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
          {list.map(v => (
            <span key={v} style={{ background: bg, color, borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{v}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── BuddyField — multi-select badge component ─────────────────────────────────

function BuddyField({ form, setForm, buddyList }) {
  const [input, setInput] = useState('');

  const selected = form.buddy
    ? form.buddy.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  const addBuddy = (name) => {
    const trimmed = name.trim();
    if (!trimmed || selected.includes(trimmed)) return;
    setForm({ ...form, buddy: [...selected, trimmed].join(', ') });
  };

  const removeBuddy = (name) => {
    setForm({ ...form, buddy: selected.filter(b => b !== name).join(', ') });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (input.trim()) { addBuddy(input); setInput(''); }
    }
    if (e.key === 'Backspace' && !input && selected.length > 0) {
      removeBuddy(selected[selected.length - 1]);
    }
  };

  const available = buddyList.filter(b => !selected.includes(b));

  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Buddy</label>

      {/* Geselecteerde buddy's als badges */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {selected.map(b => (
            <div key={b} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'var(--blue-light)', color: 'var(--blue)',
              borderRadius: 99, padding: '4px 10px', fontSize: 12, fontWeight: 600,
            }}>
              {b}
              <button type="button" onClick={() => removeBuddy(b)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--blue)', fontSize: 13, padding: 0, lineHeight: 1, opacity: 0.7,
              }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Beschikbare buddy's om op te klikken */}
      {available.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {available.map(b => (
            <button key={b} type="button" onClick={() => addBuddy(b)} style={{
              background: 'transparent', border: '1px dashed var(--border)',
              borderRadius: 99, padding: '3px 10px', fontSize: 12,
              color: 'var(--text-muted)', cursor: 'pointer',
            }}>
              + {b}
            </button>
          ))}
        </div>
      )}

      {/* Vrij tekstveld voor eigen naam */}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) { addBuddy(input); setInput(''); } }}
        placeholder={selected.length === 0 ? 'Typ naam en druk Enter…' : 'Nog een buddy toevoegen…'}
        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
      />
    </div>
  );
}

// ── BuddyInfoRow — badges in leesmodus + handtekening ────────────────────────

function BuddyInfoRow({ buddy, buddyObjects = [] }) {
  const list = buddy ? buddy.split(',').map(s => s.trim()).filter(Boolean) : [];

  // Koppel elk naam aan het bijhorende buddy-object (voor handtekening)
  const withSig = list.map(name => {
    const obj = buddyObjects.find(b => b.name.trim().toLowerCase() === name.toLowerCase());
    return { name, handtekening: obj?.handtekening || null };
  });

  const hasSig = withSig.some(b => b.handtekening);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13, gap: 8 }}>
      <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>Buddy</span>
      {list.length === 0 ? (
        <span style={{ fontWeight: 500 }}>—</span>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', alignItems: 'flex-start' }}>
          {withSig.map(({ name, handtekening }) => (
            handtekening ? (
              /* Badge met geïntegreerde handtekening */
              <div key={name} style={{
                background: 'var(--blue-light)',
                border: '1px solid var(--blue)',
                borderRadius: 8,
                padding: '4px 10px 6px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              }}>
                <span style={{ color: 'var(--blue)', fontSize: 12, fontWeight: 600, lineHeight: 1 }}>{name}</span>
                <img
                  src={`/uploads/buddies/${handtekening}.jpg`}
                  alt={`Handtekening ${name}`}
                  style={{ height: 32, maxWidth: 120, objectFit: 'contain', borderRadius: 2, background: '#fff' }}
                />
              </div>
            ) : (
              /* Gewone badge zonder handtekening */
              <span key={name} style={{
                background: 'var(--blue-light)', color: 'var(--blue)',
                borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 600,
              }}>{name}</span>
            )
          ))}
        </div>
      )}
    </div>
  );
}

// ── StampGallery — stempels & handtekeningen per duik ─────────────────────────

function StampGallery({ diveId, stamps, setStamps }) {
  const fileRef    = useRef();
  const [uploading, setUploading]       = useState(false);
  const [lightbox,  setLightbox]        = useState(null);  // stamp object
  const [editingId, setEditingId]       = useState(null);  // stamp.id
  const [nameDraft, setNameDraft]       = useState('');

  const stampUrl = (filename) => `/uploads/stamps/${filename}.jpg`;

  const handleFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('stamp', file);
        const result = await uploadDiveStamp(diveId, fd);
        setStamps(prev => [...prev, result]);
      } catch (err) {
        console.error('Stempel upload fout:', err);
      }
    }
    setUploading(false);
  };

  const handleDelete = async (stamp) => {
    if (!window.confirm(`Stempel verwijderen?`)) return;
    try {
      await deleteDiveStamp(diveId, stamp.id);
      setStamps(prev => prev.filter(s => s.id !== stamp.id));
      if (lightbox?.id === stamp.id) setLightbox(null);
    } catch {}
  };

  const startEdit = (stamp, e) => {
    e.stopPropagation();
    setEditingId(stamp.id);
    setNameDraft(stamp.dive_center || '');
  };

  const saveName = async (stamp) => {
    try {
      await updateStampName(diveId, stamp.id, nameDraft);
      setStamps(prev => prev.map(s => s.id === stamp.id ? { ...s, dive_center: nameDraft || null } : s));
      if (lightbox?.id === stamp.id) setLightbox(prev => ({ ...prev, dive_center: nameDraft || null }));
    } catch {}
    setEditingId(null);
  };

  return (
    <div style={{ padding: '20px 0' }}>

      {/* Upload-knop boven de grid */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          className="btn btn-primary"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ fontSize: 13 }}
        >
          {uploading ? '⏳ Uploaden…' : '📷 Stempel toevoegen'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Foto van stempel of handtekening in logboek
        </span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          style={{ display: 'none' }}
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {stamps.length === 0 && !uploading && (
        <div style={{
          textAlign: 'center', padding: '48px 24px',
          color: 'var(--text-muted)', fontSize: 14,
          background: 'var(--surface)', borderRadius: 12,
          border: '2px dashed var(--border)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔏</div>
          Nog geen stempels — klik op "Stempel toevoegen" om een foto van een stempel of handtekening te uploaden.
        </div>
      )}

      {stamps.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {stamps.map(stamp => (
            <div
              key={stamp.id}
              style={{ borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden', background: 'var(--card)', cursor: 'pointer' }}
              onClick={() => setLightbox(stamp)}
            >
              <img
                src={stampUrl(stamp.filename)}
                alt={stamp.dive_center || 'Stempel'}
                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
              />
              <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                {editingId === stamp.id ? (
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={e => setNameDraft(e.target.value)}
                    onBlur={() => saveName(stamp)}
                    onKeyDown={e => { if (e.key === 'Enter') saveName(stamp); if (e.key === 'Escape') setEditingId(null); }}
                    onClick={e => e.stopPropagation()}
                    placeholder="Naam duikcenter…"
                    style={{ flex: 1, fontSize: 11, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)' }}
                  />
                ) : (
                  <span
                    onClick={e => startEdit(stamp, e)}
                    style={{ flex: 1, fontSize: 11, color: stamp.dive_center ? 'var(--text)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }}
                    title="Klik om naam toe te voegen"
                  >
                    {stamp.dive_center || '+ duikcenter'}
                  </span>
                )}
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(stamp); }}
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14, padding: 2, flexShrink: 0 }}
                  title="Verwijderen"
                >🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,.92)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <img
            src={stampUrl(lightbox.filename)}
            alt={lightbox.dive_center || 'Stempel'}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,.6)' }}
          />
          {lightbox.dive_center && (
            <div style={{ marginTop: 12, color: 'rgba(255,255,255,.8)', fontSize: 14, textAlign: 'center' }}>
              📍 {lightbox.dive_center}
            </div>
          )}
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute', top: 12, right: 16,
              background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
              borderRadius: 99, width: 34, height: 34, fontSize: 17, cursor: 'pointer',
            }}
          >✕</button>
        </div>
      )}
    </div>
  );
}

// ── VideoFramePicker — frames uit video extraheren als foto's ─────────────────

function VideoFramePicker({ onClose, onUpload }) {
  const videoRef    = useRef();
  const canvasRef   = useRef();
  const fileRef     = useRef();
  const [videoSrc,  setVideoSrc]   = useState(null);
  const [frames,    setFrames]     = useState([]);   // [{ url, file }]
  const [playing,   setPlaying]    = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,  setDuration]   = useState(0);
  const [uploading, setUploading]  = useState(false);

  const loadVideo = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setVideoSrc(url);
    setFrames([]);
    setCurrentTime(0);
    setPlaying(false);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else          { v.pause(); setPlaying(false); }
  };

  const seek = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(duration, v.currentTime + delta));
  };

  const captureFrame = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    c.width  = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob(blob => {
      const ts   = v.currentTime.toFixed(2).replace('.', 's') + 'f';
      const file = new File([blob], `frame_${ts}.jpg`, { type: 'image/jpeg' });
      const url  = URL.createObjectURL(blob);
      setFrames(prev => [...prev, { url, file }]);
    }, 'image/jpeg', 0.92);
  };

  const removeFrame = (idx) => {
    setFrames(prev => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleUpload = async () => {
    if (!frames.length) return;
    setUploading(true);
    try {
      await onUpload(frames.map(f => f.file));
      frames.forEach(f => URL.revokeObjectURL(f.url));
      onClose();
    } catch (err) {
      alert('Upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Tijdsnotatie mm:ss.t
  const fmtTime = (s) => {
    if (!s && s !== 0) return '0:00.0';
    const m = Math.floor(s / 60);
    const sec = (s % 60).toFixed(1).padStart(4, '0');
    return `${m}:${sec}`;
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,.82)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--card)', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 680, maxHeight: '92vh', overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>🎬 Frames uit video</div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
        </div>

        {/* Stap 1 — video kiezen */}
        {!videoSrc && (
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              border: '2px dashed var(--border)', borderRadius: 12, padding: '36px 24px',
              textAlign: 'center', cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎥</div>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Klik om een video te kiezen</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>MP4, MOV, WebM — de video wordt niet geüpload</div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }}
          onChange={e => loadVideo(e.target.files[0])} />

        {/* Stap 2 — speler + capture */}
        {videoSrc && (
          <>
            <video
              ref={videoRef}
              src={videoSrc}
              style={{ width: '100%', borderRadius: 8, background: '#000', maxHeight: 320, objectFit: 'contain' }}
              onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
              onLoadedMetadata={e => { setDuration(e.target.duration); }}
              onEnded={() => setPlaying(false)}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Scrubber */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 56, textAlign: 'right' }}>
                {fmtTime(currentTime)}
              </span>
              <input
                type="range" min={0} max={duration || 1} step={0.05}
                value={currentTime}
                onChange={e => { videoRef.current.currentTime = parseFloat(e.target.value); }}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 48 }}>
                {fmtTime(duration)}
              </span>
            </div>

            {/* Knoppen */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => seek(-5)}>⏮ 5s</button>
              <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => seek(-1/30)}>‹ frame</button>
              <button className="btn btn-primary"  style={{ fontSize: 13, minWidth: 80 }} onClick={togglePlay}>
                {playing ? '⏸ Pauze' : '▶ Afspelen'}
              </button>
              <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => seek(1/30)}>frame ›</button>
              <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => seek(5)}>5s ⏭</button>
              <button
                className="btn btn-primary"
                style={{ fontSize: 13, marginLeft: 'auto', background: 'var(--blue)', color: '#fff' }}
                onClick={captureFrame}
              >
                📸 Frame vastleggen
              </button>
              <button className="btn btn-outline" style={{ fontSize: 11 }}
                onClick={() => fileRef.current?.click()}>
                Andere video
              </button>
            </div>
          </>
        )}

        {/* Vastgelegde frames */}
        {frames.length > 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .6 }}>
              {frames.length} frame{frames.length > 1 ? 's' : ''} vastgelegd
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {frames.map((f, i) => (
                <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                  <img src={f.url} alt={`Frame ${i + 1}`}
                    style={{ height: 80, width: 'auto', borderRadius: 6, border: '1px solid var(--border)', objectFit: 'cover' }} />
                  <button
                    onClick={() => removeFrame(i)}
                    style={{
                      position: 'absolute', top: 3, right: 3,
                      background: 'rgba(0,0,0,.6)', border: 'none', color: '#fff',
                      borderRadius: 99, width: 18, height: 18, fontSize: 10,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>✕</button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Actieknoppen onderaan */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button className="btn btn-outline" onClick={onClose}>Annuleren</button>
          <button
            className="btn btn-primary"
            disabled={!frames.length || uploading}
            onClick={handleUpload}
            style={{ opacity: !frames.length ? 0.45 : 1 }}
          >
            {uploading ? '⏳ Uploaden…' : `${frames.length} frame${frames.length !== 1 ? 's' : ''} toevoegen aan duik`}
          </button>
        </div>
      </div>
    </div>
  );
}

function DatalistField({ label, name, form, setForm, options, listId }) {
  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{label}</label>
      <input
        list={listId}
        value={form[name] ?? ''}
        onChange={e => setForm({ ...form, [name]: e.target.value })}
        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
        placeholder="Typ of kies..."
      />
      <datalist id={listId}>
        {options.map(o => <option key={o} value={o} />)}
      </datalist>
    </div>
  );
}
